import { useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  FormButton,
  FormField,
  Icon,
  Input,
  Popup,
} from "semantic-ui-react";
import { useMeetingNotesTemplates } from "../../../hooks/useMeetingNotesTemplates";
import { style } from "../../../style";
import { useAuth } from "../../../contexts/AuthContext";
import { getTimezoneOptions } from "../../../utils/timezoneOptions";
import { Card, Field } from "../../ui";

const UserForm = ({
  formErrors,
  userDetails,
  setUserDetails,
  handleSubmit,
  handleCancel,
  isLoading,
  editMode,
}) => {
  const { hasFeature } = useAuth();
  const { templates, isLoading: templatesLoading } = useMeetingNotesTemplates();
  // Zone as loaded, so a legacy saved zone stays pickable after changing it
  const [savedTimezone] = useState(userDetails?.user_timezone);
  const timezoneOptions = useMemo(
    () => getTimezoneOptions(savedTimezone),
    [savedTimezone],
  );
  const templateOptions = useMemo(
    () =>
      templates.map((template) => ({
        key: template.id,
        value: template.id,
        text: template.name,
      })),
    [templates],
  );

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Card style={{ maxWidth: "750px", padding: "20px 24px" }}>
      <Form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
        <Field
          variant="form"
          label="First name"
          error={"first_name" in formErrors && "First name is required"}
        >
          <Input
            value={userDetails?.first_name ?? ""}
            disabled={editMode}
            onChange={(_, { value }) =>
              setUserDetails({ ...userDetails, first_name: value })
            }
          />
        </Field>
        <Field
          variant="form"
          label="Last name"
          error={"last_name" in formErrors && "Last name is required"}
        >
          <Input
            value={userDetails?.last_name ?? ""}
            disabled={editMode}
            onChange={(_, { value }) =>
              setUserDetails({ ...userDetails, last_name: value })
            }
          />
        </Field>
        <Field
          variant="form"
          label="Email address"
          error={
            "email" in formErrors &&
            (formErrors.email[0] || "Email address is required")
          }
        >
          <Input
            value={userDetails?.email ?? ""}
            disabled={editMode}
            onChange={(_, { value }) => {
              setUserDetails({ ...userDetails, email: value });
            }}
          />
        </Field>
        <Field
          variant="form"
          label="iPECS extension"
          error={
            "ipecs_ext" in formErrors &&
            (formErrors.ipecs_ext[0] || "iPECS extension is required")
          }
        >
          <Input
            value={userDetails?.ipecs_ext ?? ""}
            onChange={(_, { value }) => {
              setUserDetails({ ...userDetails, ipecs_ext: value });
            }}
          />
        </Field>
        <Field
          variant="form"
          label="Timezone"
          hint={
            <>
              Will default to the customer&rsquo;s timezone
              {userDetails?.customer_timezone &&
                ` (${userDetails.customer_timezone})`}{" "}
              unless you set a user-specific timezone here
            </>
          }
          error={"user_timezone" in formErrors && formErrors.user_timezone[0]}
        >
          <Dropdown
            search
            selection
            clearable
            options={timezoneOptions}
            value={userDetails?.user_timezone ?? ""}
            placeholder="Select a timezone"
            onChange={(_, { value }) => {
              setUserDetails({ ...userDetails, user_timezone: value || null });
            }}
          />
        </Field>
        <Field
          variant="form"
          label="Meeting notes template"
          hint={
            <>
              Used when this user's calls are summarised. Leave blank to use the
              customer default.
            </>
          }
          error={
            "meeting_notes_template_id" in formErrors &&
            formErrors.meeting_notes_template_id[0]
          }
        >
          {/* Cleared/"" means "inherit the customer default" (sent as null),
              the same intent as UsersTable's INHERIT_DEFAULT option, but not
              the same as SummariseTemplatePicker's BUILT_IN, which forces the
              built-in prompt. "" is fine here because an empty value is
              exactly what a cleared dropdown should look like. */}
          <Dropdown
            search
            selection
            clearable
            loading={templatesLoading}
            disabled={templatesLoading}
            options={templateOptions}
            value={userDetails?.meeting_notes_template_id ?? ""}
            placeholder={
              templatesLoading ? "Loading templates…" : "Customer default"
            }
            onChange={(_, { value }) => {
              setUserDetails({
                ...userDetails,
                meeting_notes_template_id: value || null,
              });
            }}
          />
        </Field>
        {hasFeature(["auto_transcribe"]) && (
          <FormField
            style={{
              display: "flex",
              alignItems: "center",
              alignContent: "center",
              gap: "5px",
              marginTop: "36px",
            }}
          >
            <Checkbox
              label="Auto transcription"
              checked={userDetails?.auto_transcribe ?? false}
              toggle
              onChange={(_, { checked }) => {
                setUserDetails({
                  ...userDetails,
                  auto_transcribe: checked,
                });
              }}
            />
            <Popup
              content="Automatically transcribe this user's calls."
              basic
              trigger={<Icon name="info circle" size="small" />}
            />
          </FormField>
        )}
        <FormField
          style={{
            display: "flex",
            alignItems: "center",
            alignContent: "center",
            gap: "5px",
            marginTop: "36px",
          }}
        >
          <Checkbox
            label="Admin user"
            checked={userDetails?.admin ?? false}
            toggle
            onChange={(_, { checked }) => {
              setUserDetails({ ...userDetails, admin: checked });
            }}
          />
          <Popup
            content="Admins can add, edit and delete users and view all call transcriptions and summaries. They can also manage teams and assign and revoke sidekick licences."
            basic
            trigger={<Icon name="info circle" size="small" />}
          />
        </FormField>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "end",
            marginTop: "28px",
            gap: "10px",
          }}
        >
          <Button onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <FormButton
            type="submit"
            style={style.primaryButton}
            disabled={isLoading}
            loading={isLoading}
          >
            Save
          </FormButton>
        </div>
      </Form>
    </Card>
  );
};

export default UserForm;
